import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Case, ActionItem } from '../../../../core/models';
import { Observable, map, tap } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-case-status-card',
  standalone: true,
  imports: [CommonModule, NgClass],
  templateUrl: './case-status-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('collapseAnimation', [
      state('collapsed', style({
        height: '0',
        overflow: 'hidden',
        opacity: '0',
        paddingTop: '0',
        paddingBottom: '0',
      })),
      state('expanded', style({
        height: '*',
        overflow: 'hidden',
        opacity: '1',
      })),
      transition('collapsed <=> expanded', [
        animate('300ms ease-in-out')
      ])
    ])
  ]
})
export class CaseStatusCardComponent implements OnInit {
  @Input() caseDetails$!: Observable<Case | null>;
  isExpanded = false;
  isMobile = false;

  private destroyRef = inject(DestroyRef);
  private breakpointObserver = inject(BreakpointObserver);

  filteredActionItems$: Observable<ActionItem[]> = new Observable<ActionItem[]>();

  constructor() {
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map(result => result.matches),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(isHandset => {
      this.isMobile = isHandset;
      this.isExpanded = isHandset; // Always expanded on mobile
    });
  }

  toggle() {
    if (!this.isMobile) {
      this.isExpanded = !this.isExpanded;
    }
  }

  ngOnInit(): void {
    this.filteredActionItems$ = this.caseDetails$.pipe(
      map(details => {
        const itemsObj = details?.caseFlow?.actionItems;
        if (!itemsObj || Object.keys(itemsObj).length === 0) {
          return [];
        }
        const items: ActionItem[] = Object.values(itemsObj);
        const filtered = items.filter(item => item.sharedWith?.doctor);

        // Add sorting logic here: completed first, then pending
        filtered.sort((a, b) => {
          if (a.completedOn && !b.completedOn) {
            return -1; // a (completed) comes before b (pending)
          }
          if (!a.completedOn && b.completedOn) {
            return 1; // b (completed) comes before a (pending)
          }
          // If both are completed or both are pending, maintain original order or sort by name/date if needed
          return 0;
        });

        return filtered;
      }),
      tap(items => console.log('CaseStatusCardComponent: Filtered and sorted action items:', items)),
      takeUntilDestroyed(this.destroyRef)
    );
  }
}
